// nextQuestionAlgorithm.ts
// Strict depth-first onboarding navigation algorithm for CrowdSec
// - Always follows user-selected path (never jumps to unchosen branches)
// - Never revisits a question
// - Skipped questions: if deep dive, go up; if main topic, go to next topic
// - Ends when no valid next question is found

import { JourneyQuestion } from "@/types/dataSchema";

interface NextQuestionParams {
  questions: JourneyQuestion[];
  questionHistory: string[];
  currentQuestionId: string | null;
  skipped: boolean;
  selectedOptions: string[];
  answers: Record<string, string | string[]>;
}

/**
 * Returns the next questionId to display, or null if recommendations should be shown.
 * Follows strict depth-first then next-topic logic based on user's selected path.
 */
export function getNextQuestionId({
  questions,
  questionHistory,
  currentQuestionId,
  skipped,
  selectedOptions,
  answers,
}: NextQuestionParams): string | null {
  if (!currentQuestionId) return null;

  const visited = new Set(questionHistory);
  const questionMap = new Map(questions.map((q) => [q.id, q]));
  const current = questionMap.get(currentQuestionId);

  if (!current) return null;

  // Helper function to find all pending deeper questions from the user's path
  function findPendingDeeperQuestions(): string | null {
    // Go through history from most recent to oldest
    for (let i = questionHistory.length - 1; i >= 0; i--) {
      const questionId = questionHistory[i];
      const question = questionMap.get(questionId);
      const answer = answers[questionId];

      if (!question || !answer) continue;

      // Get selected options for this question
      const selected = Array.isArray(answer) ? answer : [answer];

      // Check each selected option for unvisited deeper questions
      for (const optValue of selected) {
        const baseValue = optValue.split(":")[0];
        const option = question.options.find((o) => o.value === baseValue);

        if (
          option &&
          option.deeperQuestionId &&
          !visited.has(option.deeperQuestionId)
        ) {
          return option.deeperQuestionId;
        }
      }
    }

    return null;
  }

  // Priority 1: Check current question's deeper questions first (if not skipped)
  if (!skipped && selectedOptions.length > 0) {
    for (const optValue of selectedOptions) {
      const baseValue = optValue.split(":")[0];
      const option = current.options.find((o) => o.value === baseValue);

      if (
        option &&
        option.deeperQuestionId &&
        !visited.has(option.deeperQuestionId)
      ) {
        return option.deeperQuestionId;
      }
    }
  }

  // Priority 2a: Check for any pending deeper questions in the entire history
  const pendingDeeper = findPendingDeeperQuestions();

  if (pendingDeeper) {
    return pendingDeeper;
  }

  // Priority 2b: Search up the history for next topic
  for (let i = questionHistory.length - 1; i >= 0; i--) {
    const parentId = questionHistory[i];
    const parent = questionMap.get(parentId);

    if (
      parent &&
      parent.nextTopicQuestionId &&
      !visited.has(parent.nextTopicQuestionId)
    ) {
      return parent.nextTopicQuestionId;
    }
  }

  // Priority 3: End of journey
  return null;
}
